<script setup lang="ts">
import type { AwTableRow } from "@asap/design";
import type { PpeRequestSchema } from "@asap/shared/schemas/ppe-request";
import { getPpeRequestStatusColor, getPpeRequestStatusLabel } from "~/components/ppe-request/ppe-request.model";
import { usePpeRequestsPage } from "./usePpeRequestsPage";

const {
  ppeRequestTableState,
  ppeRequestTabs,
  selectableColumns,
  tableKey,
  tableColumns,
  updateColumn,
  resetFilters,
  ppeRequests,
  rowSelection,
  tabValue,
  emptyStateType,
  showCreateModal,
  openedPpeRequest,
  ppeRequestToApprove,
  fetchPpeRequests,
  getRowActions,
  enableRowSelection,
  closeApproveDialog,
  handlePpeRequestApproved,
  handleSheetChange,
} = usePpeRequestsPage();

const openPpeRequest = (row: AwTableRow<PpeRequestSchema>) => {
  openedPpeRequest.value = row.original;
};
</script>

<template>
  <PageContainer>
    <Title>{{ $t("ppeRequests.page.title") }}</Title>
    <PageHeader>
      <HeaderActions>
        <AwButton color="secondary" @click="showCreateModal = true">
          <AwIcon name="i-ph-plus" />
          {{ $t("ppeRequests.page.createButton") }}
        </AwButton>
      </HeaderActions>
    </PageHeader>

    <PageBody>
      <TableToolbar
        v-model:tab-value="tabValue"
        :tabs="ppeRequestTabs"
        :selectable-columns="selectableColumns"
        :update-column="updateColumn"
      />
      <TableFiltersContainer>
        <AwTextField
          v-model="ppeRequestTableState.selectedFilters.search"
          leading-icon="i-ph-magnifying-glass"
          :placeholder="$t('ppeRequests.page.searchPlaceholder')"
          class="min-w-fit"
          :debounce-delay="200"
        />
        <TableMonthFilter v-model="ppeRequestTableState.selectedFilters.selectedDate" />
        <BusinessUnitSelect v-model:business-unit-ids="ppeRequestTableState.selectedFilters.businessUnitIds" />
        <TeamMemberSelect
          v-model:team-member-ids="ppeRequestTableState.selectedFilters.consultantTeamMemberIds"
          :roles="['consultant_team_member']"
        />
        <TeamMemberSelect
          v-model:team-member-ids="ppeRequestTableState.selectedFilters.wsmTeamMemberIds"
          :roles="['csm_team_member']"
          :title="$t('ppeRequests.page.wsmFilter')"
        />
        <TableFiltersResetButton @click="resetFilters" />
      </TableFiltersContainer>
      <TableInteractive
        :key="tableKey"
        v-model:row-selection="rowSelection"
        v-model:sorting="ppeRequestTableState.sorting"
        :enable-sorting="true"
        :manual-sorting="true"
        :on-select="openPpeRequest"
        :enable-row-selection="enableRowSelection"
        :data="ppeRequests"
        :columns="tableColumns"
        :empty-state-type="emptyStateType"
        :empty-state-reset-filters="resetFilters"
        :loading="ppeRequestTableState.loading"
      >
        <template #talent-cell="{ row }">
          <TableCellContainer :is-loading="ppeRequestTableState.loading" :skeletons="['text', 'text']">
            <TableCellTalent :talent="row.original.contract.talent" :is-loading="ppeRequestTableState.loading" />
          </TableCellContainer>
        </template>
        <template #status-cell="{ row }">
          <TableCellContainer :is-loading="ppeRequestTableState.loading" :skeletons="['status']">
            <AwBadge :color="getPpeRequestStatusColor(row.original.status)">
              {{ getPpeRequestStatusLabel(row.original.status) }}
            </AwBadge>
          </TableCellContainer>
        </template>
        <template #wsm-cell="{ row }">
          <TableCellContainer :is-loading="ppeRequestTableState.loading" :skeletons="['avatar', 'text']">
            <TableCellTeamMember :team-member="row.original.wsm" />
          </TableCellContainer>
        </template>
        <template #created_at-cell="{ row }">
          <TableCellContainer :is-loading="ppeRequestTableState.loading" :skeletons="['text']">
            <TableCellDate
              :prefix-icon="row.original.is_created_from_app ? '📱' : undefined"
              :date="row.original.created_at"
            />
          </TableCellContainer>
        </template>
        <template #items-cell="{ row }">
          <TableCellContainer :is-loading="ppeRequestTableState.loading" :skeletons="['amount']">
            <TableCellPrice :price="row.original.price" />
          </TableCellContainer>
        </template>
        <template #consultant-cell="{ row }">
          <TableCellContainer :is-loading="ppeRequestTableState.loading" :skeletons="['avatar', 'text']">
            <TableCellTeamMember :team-member="row.original.owner" />
          </TableCellContainer>
        </template>
        <template v-if="!ppeRequestTableState.loading" #primaryAction="{ row }">
          <TablePrimaryAction :action="getRowActions(row.original).at(0)" />
        </template>
        <template v-if="!ppeRequestTableState.loading" #actions="{ row }">
          <TableRowActions :row="row" :actions="getRowActions(row.original)" />
        </template>
        <template #footer>
          <TableSelectionActions :row-selection="rowSelection" @clear-selection="rowSelection = {}" />
          <TablePagination
            :total-items="ppeRequestTableState.totalItems"
            :page-size="ppeRequestTableState.selectedFilters.pageSize"
            :page-index="ppeRequestTableState.selectedFilters.pageIndex"
            @update:page-size="ppeRequestTableState.selectedFilters.pageSize = $event"
            @update:page-index="ppeRequestTableState.selectedFilters.pageIndex = $event"
          />
        </template>
      </TableInteractive>
    </PageBody>

    <LazyPpeRequestCreateModal
      v-if="showCreateModal"
      @on-create="fetchPpeRequests"
      @on-close="showCreateModal = false"
    />
    <LazyPpeRequestApproveDialog
      v-if="ppeRequestToApprove"
      :ppe-request="ppeRequestToApprove"
      @on-approve="handlePpeRequestApproved"
      @on-close="closeApproveDialog"
    />
    <LazyPpeRequestShowSheet
      v-if="openedPpeRequest"
      :ppe-request="openedPpeRequest"
      @on-close="openedPpeRequest = undefined"
      @on-change="handleSheetChange"
    />
  </PageContainer>
</template>
